// Encrypted file sharing (F-COM-5, design side panel "Files"). The file is
// encrypted in the browser under a per-file key derived from the room secret;
// only ciphertext is uploaded to S3 via a presigned URL; the reference travels
// over the E2EE data channel. Receivers fetch the ciphertext and decrypt
// locally. The server never has the key — the object is undecryptable
// server-side.

import { MaxFileSize } from '@brizina/call-engine';
import { ChangeEvent, memo, MouseEvent, useCallback, useRef } from 'react';

import { api } from '../api';
import { engine } from '../engine';
import { probeRender } from '../perf/renderProbe';
import {
  addSharedFile,
  callStore,
  pushToast,
  updateSharedFile,
  useCallStore,
} from '../store/callStore';
import { IconButton } from '../ui/IconButton';
import { IconClose, IconDownload, IconLockedDesktop, IconUpload } from '../ui/icons';

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase().slice(0, 4) : 'FILE';
}

export const FilesPanel = memo(function FilesPanel({ onClose }: { onClose: () => void }) {
  probeRender('FilesPanel');
  const files = useCallStore((s) => s.sharedFiles);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = useCallback(() => inputRef.current?.click(), []);

  const onFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    void shareFile(file);
  }, []);

  const download = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const fileId = e.currentTarget.dataset.fileId!;
    void downloadFile(fileId);
  }, []);

  return (
    <div className="bz-panel bz-files-panel" role="region" aria-label="Shared files">
      <div className="bz-panel-header">
        <span>Files</span>
        <div className="bz-panel-header-actions">
          <IconButton shape="flat" label="Close" onClick={onClose}>
            <IconClose size={14} />
          </IconButton>
        </div>
      </div>
      <div className="bz-files-note">
        <IconLockedDesktop size={16} />
        <span>
          Files are end-to-end encrypted before upload — our servers only ever store ciphertext,
          and they expire automatically.
        </span>
      </div>
      <div className="bz-files-list">
        {files.map((f) => (
          <div key={f.fileId} className="bz-file-row">
            <span className="bz-file-ext">{extOf(f.name)}</span>
            <div className="bz-rowmain">
              <div className="bz-file-name" title={f.name}>
                {f.name}
              </div>
              <div className="bz-file-meta">
                {formatSize(f.size)} · {f.outgoing ? 'you' : f.fromName} · expires in 24h
              </div>
            </div>
            {f.status === 'ready' || f.status === 'downloading' ? (
              <IconButton
                shape="flat"
                label={f.status === 'downloading' ? 'Decrypting…' : 'Download'}
                data-file-id={f.fileId}
                onClick={download}
                disabled={f.status === 'downloading'}
              >
                <IconDownload size={15} />
              </IconButton>
            ) : (
              <span className="bz-file-status">
                {f.status === 'sharing' ? 'Encrypting…' : 'Failed'}
              </span>
            )}
          </div>
        ))}
      </div>
      <input ref={inputRef} type="file" hidden onChange={onFile} />
      <div className="bz-files-actions">
        <button type="button" className="bz-files-share" onClick={pick}>
          <IconUpload size={15} />
          Share a file
        </button>
      </div>
    </div>
  );
});

async function shareFile(file: File): Promise<void> {
  const token = callStore.getState().roomToken;
  if (!token) {
    pushToast('File sharing needs an encrypted meeting', 'warning');
    return;
  }
  const data = await file.arrayBuffer();
  const { fileId, ciphertext, nonceB64 } = await engine.encryptFileForSharing(data);
  if (ciphertext.byteLength > MaxFileSize) {
    pushToast(`Files must be under ${MaxFileSize >> 20} MB`, 'warning');
    return;
  }
  addSharedFile({
    fileId,
    objectKey: '',
    name: file.name,
    size: ciphertext.byteLength,
    mime: file.type || 'application/octet-stream',
    nonceB64,
    ts: Date.now(),
    fromName: 'you',
    outgoing: true,
    status: 'sharing',
  });
  try {
    const { uploadUrl, objectKey } = await api.presignUpload(token, fileId, ciphertext.byteLength);
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      body: ciphertext,
      headers: { 'Content-Type': 'application/octet-stream' },
    });
    if (!put.ok) throw new Error(`upload failed (${put.status})`);
    await engine.broadcastFileRef({
      fileId,
      objectKey,
      name: file.name,
      size: ciphertext.byteLength,
      mime: file.type || 'application/octet-stream',
      nonceB64,
    });
    updateSharedFile(fileId, { objectKey, status: 'ready' });
  } catch (err) {
    updateSharedFile(fileId, { status: 'failed' });
    pushToast(err instanceof Error ? err.message : 'Could not share the file', 'error');
  }
}

async function downloadFile(fileId: string): Promise<void> {
  const token = callStore.getState().roomToken;
  const f = callStore.getState().sharedFiles.find((x) => x.fileId === fileId);
  if (!token || !f) return;
  updateSharedFile(fileId, { status: 'downloading' });
  try {
    const { downloadUrl } = await api.presignDownload(token, f.objectKey);
    const res = await fetch(downloadUrl);
    if (!res.ok) throw new Error(`download failed (${res.status})`);
    const ciphertext = await res.arrayBuffer();
    const plaintext = await engine.decryptSharedFile(fileId, f.nonceB64, ciphertext);
    saveBlob(new Blob([plaintext], { type: f.mime }), f.name);
    updateSharedFile(fileId, { status: 'ready' });
  } catch (err) {
    updateSharedFile(fileId, { status: 'ready' });
    pushToast(err instanceof Error ? err.message : 'Could not open the file', 'error');
  }
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function formatSize(bytes: number): string {
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${Math.round(bytes / (1 << 10))} KB`;
  return `${bytes} B`;
}
